import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, ArrowRight, Hand, CheckCircle2, FlaskConical, ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { API } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Reveal } from "@/components/landing/Reveal";
import { InterestDialog } from "@/components/landing/InterestDialog";
import { useBrochure } from "@/components/BrochureGate";
import { COURSE_LANDING } from "@/data/courseLanding";
import CourseLanding from "./CourseLanding";

const List = ({ items, testId }) => (
  <ul data-testid={testId} className="space-y-2">
    {(items || []).map((t) => <li key={t} className="flex gap-2.5 text-[15px] text-slate-800"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9384]" />{t}</li>)}
  </ul>
);

export const Module = ({ m, selfPaced }) => (
  <AccordionItem value={`m${m.n}`} className="mb-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
    <AccordionTrigger data-testid={`module-trigger-${m.n}`} className="gap-4 px-5 py-4 text-left hover:no-underline">
      <span className="flex items-center gap-4">
        <span className={`font-display grid h-12 w-12 shrink-0 place-items-center rounded-xl text-[18px] font-bold text-white ${selfPaced ? "bg-[#0E9384]" : "bg-[#0B1B33]"}`}>{String(m.n).padStart(2, "0")}</span>
        <span>
          <span className="font-display block text-[17px] font-bold leading-tight text-[#0B1B33]">{m.title}</span>
          <span className="mt-0.5 block text-[14px] font-normal italic text-slate-600">{m.why}</span>
        </span>
      </span>
    </AccordionTrigger>
    <AccordionContent className="px-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div><p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-600">What we cover</p>
          <ul className="list-disc space-y-1 pl-5 text-[14px] text-slate-800">{m.topics.map((t) => <li key={t}>{t}</li>)}</ul></div>
        <div className="rounded-xl bg-teal-50/70 p-4"><p className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#0E9384]"><FlaskConical className="h-3.5 w-3.5" /> Hands-on labs</p>
          <ul className="space-y-1.5 text-[14px] text-slate-800">{m.labs.map((t) => <li key={t} className="flex gap-2"><ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-[#0E9384]" />{t}</li>)}</ul></div>
      </div>
    </AccordionContent>
  </AccordionItem>
);

export default function CoursePage() {
  const { key } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [interest, setInterest] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/curricula/${key}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [key]);

  const { request } = useBrochure();
  const pdf = () => request({ key: c.key, title: c.title });
  const phases = Array.isArray(c?.phases) ? c.phases : [];
  const total = phases.reduce((a, p) => a + (p.modules?.length || 0), 0);

  if (c && COURSE_LANDING[key]) return <CourseLanding c={c} L={COURSE_LANDING[key]} />;
  return (
    <div data-testid="course-page" className="min-h-screen bg-[#F7FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-[1200px] items-center justify-between gap-3 px-5 lg:px-10">
          <Logo />
          {c && <button data-testid="course-header-download-btn" onClick={pdf} className="btn-teal flex items-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-semibold"><Download className="h-4 w-4" /><span className="hidden sm:inline">Download curriculum</span><span className="sm:hidden">PDF</span></button>}
        </div>
      </header>
      {err && <p data-testid="course-error" className="mx-auto max-w-xl p-10 text-center text-slate-700">Curriculum not found.</p>}
      {c && (
        <>
          <section className="grain relative overflow-hidden bg-[#0B1B33]">
            <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full border-[40px] border-[#0E9384]/60" />
            <div className="relative mx-auto max-w-[1200px] px-5 pb-14 pt-10 lg:px-10">
              <button data-testid="course-back-btn" onClick={() => nav("/go#pathway")} className="mb-8 flex items-center gap-1.5 text-[14px] text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to the pathway</button>
              <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-teal-300">FDE learning pathway · Step {c.step} of 4</p>
              <h1 className="font-display mt-3 overflow-hidden text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
                <motion.span className="block" initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.2, 0.75, 0.15, 1] }}>{c.title}</motion.span>
              </h1>
              <p className="mt-5 max-w-2xl text-base text-slate-300 md:text-lg">{c.tagline}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button data-testid="course-download-btn" onClick={pdf} className="btn-teal flex items-center justify-center gap-2 rounded-xl px-7 py-4 text-[16px] font-semibold"><Download className="h-5 w-5" /> Download curriculum PDF</button>
                <button data-testid="course-interest-btn" onClick={() => setInterest({ key: c.key, n: c.step, title: c.title, fee: c.fee })} className="flex items-center justify-center gap-2 rounded-xl border border-white/30 px-7 py-4 text-[16px] font-semibold text-white transition-colors hover:bg-white/10"><Hand className="h-5 w-5" /> I'm interested</button>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
                {(c.format || []).map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10"><p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-300">{k}</p><p className="mt-1 text-[15px] font-semibold text-white">{v}</p></div>
                ))}
              </div>
            </div>
          </section>
          <main className="mx-auto max-w-[1200px] space-y-14 px-5 py-14 lg:px-10">
            <Reveal className="grid gap-8 lg:grid-cols-[1fr_1fr]">
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">Why this course</p>
                <h2 className="font-display mt-2 text-[28px] font-bold leading-tight text-[#0B1B33] md:text-[34px]">Work forward from the problem, not backward from a framework name.</h2>
              </div>
              <div className="space-y-4 text-base leading-relaxed text-slate-700">{(c.story || []).map((p) => <p key={p}>{p}</p>)}</div>
            </Reveal>
            <div className="grid gap-5 md:grid-cols-3">
              {[["Who this is for", c.who, "course-who"], ["Before you start", c.prereqs, "course-prereqs"], ["What you'll be able to do", c.outcomes, "course-outcomes"]].map(([t, items, id], i) => (
                <Reveal key={t} delay={i * 0.08}><div className={`h-full rounded-2xl border p-6 ${i === 2 ? "border-teal-100 bg-teal-50/60" : "border-slate-200 bg-white"}`}>
                  <h3 className="font-display mb-4 text-[19px] font-bold text-[#0B1B33]">{t}</h3><List items={items} testId={id} /></div></Reveal>
              ))}
            </div>
            <Reveal>
              <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">Tools you will use</p>
              <div data-testid="course-tools" className="mt-3 flex flex-wrap gap-2">{(c.tools || []).map((t) => <span key={t} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[14px] font-medium text-[#0B1B33]">{t}</span>)}</div>
            </Reveal>
            <section data-testid="course-syllabus">
              <Reveal className="flex flex-wrap items-end justify-between gap-3">
                <div><p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">Full syllabus</p>
                  <h2 className="font-display mt-2 text-[28px] font-bold text-[#0B1B33] md:text-[34px]">{total} modules, {phases.length} phases</h2></div>
                <button data-testid="course-syllabus-download-btn" onClick={pdf} className="flex items-center gap-2 text-[15px] font-semibold text-[#0E9384] hover:underline"><Download className="h-4 w-4" /> Download as PDF</button>
              </Reveal>
              <div className="mt-6 space-y-8">
                {phases.map((ph, i) => (
                  <Reveal key={ph.name}>
                    <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
                      <div className="lg:sticky lg:top-24 lg:self-start">
                        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-600">Phase {i + 1}</p>
                        <h3 className="font-display text-[22px] font-bold leading-tight text-[#0B1B33]">{ph.name}</h3>
                        <p className="mt-1 text-[14px] text-slate-600">{ph.modules.length} module{ph.modules.length > 1 ? "s" : ""}{ph.self_paced ? " · self-paced" : ""}</p>
                      </div>
                      <Accordion type="multiple" defaultValue={i === 0 ? [`m${ph.modules[0].n}`] : []}>
                        {ph.modules.map((m) => <Module key={m.n} m={m} selfPaced={ph.self_paced} />)}
                      </Accordion>
                    </div>
                  </Reveal>
                ))}
              </div>
            </section>
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#0E9384]">Our honest promise</p>
                <p className="mt-3 text-base leading-relaxed text-slate-800 md:text-lg">{c.honest}</p>
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="course-cta" className="flex flex-col gap-6 rounded-2xl bg-[#0B1B33] p-7 md:flex-row md:items-center md:justify-between md:p-10">
                <div>
                  <p className="font-display text-[26px] font-bold text-white">Course fee {c.fee}</p>
                  <p className="mt-1 max-w-lg text-[15px] text-slate-300">Not sure this is your right stage? Take the free 5-minute diagnostic and get a suggested starting course.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button data-testid="course-cta-diagnostic-btn" onClick={() => nav("/go?diagnostic=1")} className="rounded-xl border border-white/30 px-6 py-3.5 font-semibold text-white hover:bg-white/10">Take the diagnostic</button>
                  <button data-testid="course-cta-interest-btn" onClick={() => setInterest({ key: c.key, n: c.step, title: c.title, fee: c.fee })} className="btn-teal flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-semibold">I'm interested <ArrowRight className="arrow h-4 w-4" /></button>
                </div>
              </div>
              <p className="mt-3 text-center text-[13px] text-slate-600">Dates, format, tool costs and refund terms are confirmed before any payment. No job, salary or certification outcome is guaranteed.</p>
            </Reveal>
          </main>
        </>
      )}
      <InterestDialog course={interest} onClose={() => setInterest(null)} />
    </div>
  );
}
